import { getAllUsersSubscriptions } from '../../actions/AdminActions'
import {
  createCityGroup,
  deleteCityGroup,
  getAllCityGroups,
  updateCityGroup,
} from '../../actions/CityGroupActions'
import useLanguage from '../../state/hooks/useLanguage'
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Layers as LayersIcon,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { List, Map } from 'immutable'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'

interface LangEntry {
  lang: string
  value: string
}

interface CityLang {
  name_new?: LangEntry[]
  country_new?: LangEntry[]
  state_new?: LangEntry[]
}

interface City {
  id: number
  name: string
  country: string
  province?: string | null
  lang?: CityLang | null
}

interface CityGroup {
  id: number
  name: string
  description: string
  cities: number[]
}

interface TableColumn {
  id: string
  label: string
  visible: boolean
  sortable?: boolean
}

type SortDirection = 'asc' | 'desc'

function useTableControls<T extends Record<string, unknown>>(
  data: T[],
  defaultColumns: TableColumn[],
  defaultRowsPerPage = 10
) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [columns, setColumns] = useState<TableColumn[]>(defaultColumns)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Sync column labels when defaultColumns change (e.g. language switch)
  useEffect(() => {
    setColumns((prev) =>
      prev.map((col) => {
        const updated = defaultColumns.find((d) => d.id === col.id)
        return updated ? { ...col, label: updated.label } : col
      })
    )
  }, [defaultColumns])

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Named handle* so JSX handler-name rules are satisfied at call sites
  const handleToggleColumn = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(columnId)
      setSortDirection('asc')
    }
    setPage(0)
  }

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  )

  const sortedData = useMemo(() => {
    if (!sortBy) return data
    return [...data].sort((a, b) => {
      const aVal = a[sortBy]
      const bVal = b[sortBy]
      // Use explicit triple-equals null checks
      const aIsNil = aVal === null || aVal === undefined
      const bIsNil = bVal === null || bVal === undefined
      if (aIsNil && bIsNil) return 0
      if (aIsNil) return 1
      if (bIsNil) return -1
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.toLowerCase().localeCompare(bVal.toLowerCase())
          : bVal.toLowerCase().localeCompare(aVal.toLowerCase())
      }
      // Avoid nested ternary — use explicit branches
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortBy, sortDirection])

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage
    return sortedData.slice(start, start + rowsPerPage)
  }, [sortedData, page, rowsPerPage])

  return {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    handleToggleColumn,
    handleSort,
  }
}

function ColumnVisibilityButton({
  columns,
  onToggleColumn,
}: {
  readonly columns: TableColumn[]
  readonly onToggleColumn: (id: string) => void
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        sx={{ color: '#fff' }}
      >
        <ViewColumnIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { minWidth: 200, bgcolor: '#31353D', color: '#fff' },
        }}
      >
        {columns.map((col) => (
          <MenuItem key={col.id} dense>
            <FormControlLabel
              control={
                <Checkbox
                  checked={col.visible}
                  onChange={() => onToggleColumn(col.id)}
                  size="small"
                />
              }
              label={col.label}
              sx={{ width: '100%', m: 0 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

function TableFooter({
  page,
  rowsPerPage,
  totalCount,
  onChangePage,
  onChangeRowsPerPage,
}: {
  readonly page: number
  readonly rowsPerPage: number
  readonly totalCount: number
  readonly onChangePage: (event: unknown, newPage: number) => void
  readonly onChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '1px solid rgba(0, 0, 0, 0.108214)',
      }}
    >
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ color: '#fff' }}
        showFirstButton
        showLastButton
      />
    </Box>
  )
}

interface Props {
  readonly actions: {
    readonly getAllUsersSubscriptions: () => void
    readonly getAllCityGroups: () => void
    readonly createCityGroup: (
      name: string,
      description: string,
      cityIds: number[]
    ) => void
    readonly updateCityGroup: (
      id: number,
      name: string,
      description: string,
      cityIds: number[]
    ) => void
    readonly deleteCityGroup: (id: number) => void
  }
  readonly adminStore: Map<string, List<Map<string, unknown>>>
}

const CitiesAdmin: React.FC<Props> = ({ actions, adminStore }) => {
  const intl = useIntl()
  const { language } = useLanguage()

  const getLocalizedName = (city: City): string => {
    const locale = language.locale
    const lang = city.lang
    if (lang?.name_new && locale) {
      const found = lang.name_new.find((i) => i.lang === locale)
      if (found) return found.value
    }
    return city.name
  }

  const getLocalizedCountry = (city: City): string => {
    const locale = language.locale
    const lang = city.lang
    if (lang?.country_new && locale) {
      const found = lang.country_new.find((i) => i.lang === locale)
      if (found) return found.value
    }
    return city.country
  }

  const getLocalizedState = (city: City): string => {
    const locale = language.locale
    const lang = city.lang
    if (lang?.state_new && locale) {
      const found = lang.state_new.find((i) => i.lang === locale)
      if (found) return found.value
    }
    // Fallback to first entry if user's locale isn't in state_new
    if (lang?.state_new && lang.state_new.length > 0) {
      return lang.state_new[0].value
    }
    // Fallback to the plain province column
    return city.province ?? ''
  }

  const [searchGroupQuery, setSearchGroupQuery] = useState('')
  const [searchCityQuery, setSearchCityQuery] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<CityGroup | null>(null)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [selectedCities, setSelectedCities] = useState<number[]>([])
  const [modalCitySearch, setModalCitySearch] = useState('')
  const [modalCountryFilter, setModalCountryFilter] = useState('')
  const [modalStateFilter, setModalStateFilter] = useState('')
  const [modalCitySortBy, setModalCitySortBy] = useState<string>('name')
  const [modalCitySortDir, setModalCitySortDir] = useState<SortDirection>('asc')

  useEffect(() => {
    // AllCities is an Immutable List — check size rather than truthiness
    if (adminStore.get('allCities').size === 0) {
      actions.getAllUsersSubscriptions()
    }
    actions.getAllCityGroups()
  }, [actions, adminStore])

  const cities = useMemo<City[]>(
    () =>
      (
        adminStore.get('allCities').toJS() as {
          id: number
          name: string
          country: string
          profile?: { lang?: CityLang }
        }[]
      ).map((c) => ({
        id: c.id,
        name: c.name,
        country: c.country,
        lang: c.profile?.lang ?? null,
      })),
    [adminStore]
  )

  // Mark loading complete once cities data arrives in the store
  useEffect(() => {
    if (cities.length > 0) {
      setLoading(false)
    }
  }, [cities])

  const cityGroups = useMemo<CityGroup[]>(
    () => adminStore.get('cityGroups').toJS() as CityGroup[],
    [adminStore]
  )

  const filteredGroups = useMemo(
    () =>
      cityGroups.filter(
        (g) =>
          g.name.toLowerCase().includes(searchGroupQuery.toLowerCase()) ||
          g.description.toLowerCase().includes(searchGroupQuery.toLowerCase())
      ),
    [cityGroups, searchGroupQuery]
  )

  const filteredCities = useMemo(
    () =>
      cities.filter((city) => {
        const matchesSearch =
          getLocalizedName(city)
            .toLowerCase()
            .includes(searchCityQuery.toLowerCase()) ||
          getLocalizedCountry(city)
            .toLowerCase()
            .includes(searchCityQuery.toLowerCase())
        const matchesCountry =
          !countryFilter || getLocalizedCountry(city) === countryFilter
        return matchesSearch && matchesCountry
      }),
    [cities, searchCityQuery, countryFilter]
  )

  const groupColumns = useMemo<TableColumn[]>(
    () => [
      {
        id: 'name',
        label: intl.formatMessage({ id: 'citiesAdmin.groups.table.name' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'description',
        label: intl.formatMessage({
          id: 'citiesAdmin.groups.table.description',
        }),
        visible: true,
        sortable: true,
      },
      {
        id: 'cities',
        label: intl.formatMessage({ id: 'citiesAdmin.groups.table.cities' }),
        visible: true,
        sortable: false,
      },
      {
        id: 'actions',
        label: intl.formatMessage({ id: 'citiesAdmin.groups.table.actions' }),
        visible: true,
        sortable: false,
      },
    ],
    [intl]
  )

  const cityColumns = useMemo<TableColumn[]>(
    () => [
      {
        id: 'name',
        label: intl.formatMessage({ id: 'citiesAdmin.cities.table.name' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'state',
        label: intl.formatMessage({
          id: 'citiesAdmin.cities.table.state',
          defaultMessage: 'State',
        }),
        visible: true,
        sortable: false,
      },
      {
        id: 'country',
        label: intl.formatMessage({ id: 'citiesAdmin.cities.table.country' }),
        visible: true,
        sortable: true,
      },
    ],
    [intl]
  )

  const groupControls = useTableControls(
    filteredGroups as unknown as Record<string, unknown>[],
    groupColumns
  )
  const cityControls = useTableControls(
    filteredCities as unknown as Record<string, unknown>[],
    cityColumns
  )

  const handleEditGroup = (group: CityGroup) => {
    setSelectedGroup(group)
    setGroupName(group.name)
    setGroupDescription(group.description)
    setSelectedCities(group.cities)
    setIsGroupModalOpen(true)
  }

  const handleCreateGroup = () => {
    setSelectedGroup(null)
    setGroupName('')
    setGroupDescription('')
    setSelectedCities([])
    setIsGroupModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsGroupModalOpen(false)
    setSelectedGroup(null)
    setGroupName('')
    setGroupDescription('')
    setSelectedCities([])
    setModalCitySearch('')
    setModalCountryFilter('')
    setModalStateFilter('')
    setModalCitySortBy('name')
    setModalCitySortDir('asc')
  }

  const handleSaveGroup = () => {
    if (selectedGroup) {
      actions.updateCityGroup(
        selectedGroup.id,
        groupName,
        groupDescription,
        selectedCities
      )
    } else {
      actions.createCityGroup(groupName, groupDescription, selectedCities)
    }
    handleCloseModal()
  }

  const handleDeleteGroup = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    actions.deleteCityGroup(groupId)
  }

  const handleToggleCity = (cityId: number) => {
    setSelectedCities((prev) =>
      prev.includes(cityId)
        ? prev.filter((id) => id !== cityId)
        : [...prev, cityId]
    )
  }

  const handleToggleAllCities = (checked: boolean) => {
    setSelectedCities(checked ? cities.map((c) => c.id) : [])
  }

  const uniqueCountries = useMemo(
    () =>
      [...new Set(cities.map((c) => getLocalizedCountry(c)))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [cities, language]
  )

  /*
   * Province/State options narrow to whatever country is currently selected.
   * When no country is chosen, show every state across the dataset. Empty
   * state values (cities with no localized state entry) are filtered out.
   */
  const modalUniqueStates = useMemo(
    () =>
      Array.from(
        new Set(
          cities
            .filter(
              (c) =>
                !modalCountryFilter ||
                getLocalizedCountry(c) === modalCountryFilter
            )
            .map((c) => getLocalizedState(c))
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [cities, modalCountryFilter, language]
  )

  const modalFilteredCities = useMemo(() => {
    const filtered = cities.filter((c) => {
      const matchesSearch =
        getLocalizedName(c)
          .toLowerCase()
          .includes(modalCitySearch.toLowerCase()) ||
        getLocalizedCountry(c)
          .toLowerCase()
          .includes(modalCitySearch.toLowerCase())
      const matchesCountry =
        !modalCountryFilter || getLocalizedCountry(c) === modalCountryFilter
      const matchesState =
        !modalStateFilter || getLocalizedState(c) === modalStateFilter
      return matchesSearch && matchesCountry && matchesState
    })
    return [...filtered].sort((a, b) => {
      const aVal =
        modalCitySortBy === 'country'
          ? getLocalizedCountry(a)
          : getLocalizedName(a)
      const bVal =
        modalCitySortBy === 'country'
          ? getLocalizedCountry(b)
          : getLocalizedName(b)
      const cmp = (aVal || '')
        .toLowerCase()
        .localeCompare((bVal || '').toLowerCase())
      return modalCitySortDir === 'asc' ? cmp : -cmp
    })
  }, [
    cities,
    modalCitySearch,
    modalCountryFilter,
    modalStateFilter,
    modalCitySortBy,
    modalCitySortDir,
    language,
  ])

  const handleModalCitySort = (col: string) => {
    if (modalCitySortBy === col) {
      setModalCitySortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setModalCitySortBy(col)
      setModalCitySortDir('asc')
    }
  }

  return (
    <Box
      sx={{
        maxWidth: 1800,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 10, md: 4 },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ color: '#fff' }}
        >
          <FormattedMessage id="citiesAdmin.page.title" />
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          <FormattedMessage id="citiesAdmin.page.subtitle" />
        </Typography>
      </Box>

      {/* City Groups Table */}
      <Card sx={{ mb: 4, bgcolor: '#31353D', color: '#fff' }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff' }}>
            <FormattedMessage id="citiesAdmin.groups.title" />
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateGroup}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              bgcolor: '#9EC73D',
              color: '#000',
              '&:hover': { bgcolor: '#8ab534' },
            }}
          >
            <FormattedMessage id="citiesAdmin.groups.create" />
          </Button>
        </Box>

        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            fullWidth
            disabled={loading}
            placeholder={intl.formatMessage({
              id: 'citiesAdmin.groups.searchPlaceholder',
            })}
            value={searchGroupQuery}
            onChange={(e) => setSearchGroupQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': { color: '#fff' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                { borderColor: 'rgba(255,255,255,0.5)' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                { borderColor: '#9EC73D' },
            }}
          />
          <ColumnVisibilityButton
            columns={groupControls.columns}
            onToggleColumn={groupControls.handleToggleColumn}
          />
        </Box>

        <TableContainer>
          <Table
            sx={{
              '& .MuiTableCell-root': {
                color: '#fff',
                borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
              },
              '& .MuiTableCell-head': {
                bgcolor: '#31353D',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 500,
              },
              '& .MuiTableSortLabel-root': { color: '#fff' },
              '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
              '& .MuiTableSortLabel-icon': { color: '#fff !important' },
            }}
          >
            <TableHead>
              <TableRow>
                {groupControls.visibleColumns.map((col) => (
                  <TableCell key={col.id}>
                    {col.sortable !== false ? (
                      <TableSortLabel
                        active={groupControls.sortBy === col.id}
                        direction={
                          groupControls.sortBy === col.id
                            ? groupControls.sortDirection
                            : 'asc'
                        }
                        onClick={() => groupControls.handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={groupControls.visibleColumns.length}
                    sx={{ py: 6, textAlign: 'center', border: 0 }}
                  >
                    <CircularProgress size={28} />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}
                    >
                      Loading city groups...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && groupControls.paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={groupControls.visibleColumns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      <FormattedMessage id="citiesAdmin.groups.empty" />
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading &&
                groupControls.paginatedData.length > 0 &&
                (groupControls.paginatedData as unknown as CityGroup[]).map(
                  (group) => (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.05) !important',
                        },
                      }}
                      onClick={() => handleEditGroup(group)}
                    >
                      {groupControls.visibleColumns.map((col) => {
                        if (col.id === 'name') {
                          return (
                            <TableCell key={col.id}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 1,
                                    bgcolor: 'rgba(170, 209, 48, 0.1)',
                                    borderRadius: 2,
                                  }}
                                >
                                  <LayersIcon sx={{ color: '#9EC73D' }} />
                                </Box>
                                <Typography variant="body1">
                                  {group.name}
                                </Typography>
                              </Box>
                            </TableCell>
                          )
                        }
                        if (col.id === 'description') {
                          return (
                            <TableCell key={col.id}>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.7)' }}
                              >
                                {group.description}
                              </Typography>
                            </TableCell>
                          )
                        }
                        if (col.id === 'cities') {
                          return (
                            <TableCell key={col.id}>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.7)' }}
                              >
                                {intl.formatMessage(
                                  { id: 'citiesAdmin.groups.cities' },
                                  { count: group.cities.length }
                                )}
                              </Typography>
                            </TableCell>
                          )
                        }
                        if (col.id === 'actions') {
                          return (
                            <TableCell key={col.id}>
                              <Box
                                sx={{ display: 'flex', gap: 1 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditGroup(group)}
                                  sx={{ color: '#9EC73D' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleDeleteGroup(group.id, e)
                                  }
                                  sx={{ color: '#e85866' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          )
                        }
                        return null
                      })}
                    </TableRow>
                  )
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <TableFooter
          page={groupControls.page}
          rowsPerPage={groupControls.rowsPerPage}
          totalCount={filteredGroups.length}
          onChangePage={groupControls.handleChangePage}
          onChangeRowsPerPage={groupControls.handleChangeRowsPerPage}
        />
      </Card>

      {/* All Cities Table */}
      <Card sx={{ bgcolor: '#31353D', color: '#fff' }}>
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff' }}>
            <FormattedMessage id="citiesAdmin.cities.title" />
          </Typography>
        </Box>

        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              disabled={loading}
              placeholder={intl.formatMessage({
                id: 'citiesAdmin.cities.searchPlaceholder',
              })}
              value={searchCityQuery}
              onChange={(e) => setSearchCityQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                  { borderColor: 'rgba(255,255,255,0.5)' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  { borderColor: '#9EC73D' },
              }}
            />
            <ColumnVisibilityButton
              columns={cityControls.columns}
              onToggleColumn={cityControls.handleToggleColumn}
            />
          </Box>

          <FormControl
            fullWidth
            disabled={loading}
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#9EC73D' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                { borderColor: 'rgba(255,255,255,0.5)' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                { borderColor: '#9EC73D' },
              '& .MuiSelect-select': { color: '#fff' },
              '& .MuiSelect-icon': { color: '#fff' },
            }}
          >
            <InputLabel>
              <FormattedMessage id="citiesAdmin.cities.filter.country" />
            </InputLabel>
            <Select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              label={intl.formatMessage({
                id: 'citiesAdmin.cities.filter.country',
              })}
            >
              <MenuItem value="">
                <FormattedMessage id="citiesAdmin.cities.filter.allCountries" />
              </MenuItem>
              {uniqueCountries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table
            sx={{
              '& .MuiTableCell-root': {
                color: '#fff',
                borderBottom: '1px solid rgba(0, 0, 0, 0.108214)',
              },
              '& .MuiTableCell-head': {
                bgcolor: '#31353D',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 500,
              },
              '& .MuiTableSortLabel-root': { color: '#fff' },
              '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
              '& .MuiTableSortLabel-icon': { color: '#fff !important' },
              '& .MuiTableCell-root:first-of-type': { pl: '38px' },
            }}
          >
            <TableHead>
              <TableRow>
                {cityControls.visibleColumns.map((col) => (
                  <TableCell key={col.id}>
                    {col.sortable !== false ? (
                      <TableSortLabel
                        active={cityControls.sortBy === col.id}
                        direction={
                          cityControls.sortBy === col.id
                            ? cityControls.sortDirection
                            : 'asc'
                        }
                        onClick={() => cityControls.handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={cityControls.visibleColumns.length}
                    sx={{ py: 6, textAlign: 'center', border: 0 }}
                  >
                    <CircularProgress size={28} />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}
                    >
                      Loading cities...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && cityControls.paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={cityControls.visibleColumns.length}
                    sx={{ py: 6, textAlign: 'center', border: 0 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      No cities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading &&
                cityControls.paginatedData.length > 0 &&
                (cityControls.paginatedData as unknown as City[]).map(
                  (city) => (
                    <TableRow
                      key={city.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.05) !important',
                        },
                      }}
                    >
                      {cityControls.visibleColumns.map((col) => {
                        if (col.id === 'name') {
                          return (
                            <TableCell key={col.id}>
                              {getLocalizedName(city)}
                            </TableCell>
                          )
                        }
                        if (col.id === 'state') {
                          return (
                            <TableCell key={col.id}>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.7)' }}
                              >
                                {getLocalizedState(city)}
                              </Typography>
                            </TableCell>
                          )
                        }
                        if (col.id === 'country') {
                          return (
                            <TableCell key={col.id}>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.7)' }}
                              >
                                {getLocalizedCountry(city)}
                              </Typography>
                            </TableCell>
                          )
                        }
                        return null
                      })}
                    </TableRow>
                  )
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <TableFooter
          page={cityControls.page}
          rowsPerPage={cityControls.rowsPerPage}
          totalCount={filteredCities.length}
          onChangePage={cityControls.handleChangePage}
          onChangeRowsPerPage={cityControls.handleChangeRowsPerPage}
        />
      </Card>

      {/* Create/Edit Group Dialog */}
      <Dialog
        open={isGroupModalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#1a2332', backgroundImage: 'none', color: '#fff' },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {selectedGroup
              ? intl.formatMessage({ id: 'citiesAdmin.modal.editTitle' })
              : intl.formatMessage({ id: 'citiesAdmin.modal.createTitle' })}
            <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={intl.formatMessage({ id: 'citiesAdmin.modal.groupName' })}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'citiesAdmin.modal.groupNamePlaceholder',
              })}
              fullWidth
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#9EC73D' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                  { borderColor: 'rgba(255,255,255,0.5)' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  { borderColor: '#9EC73D' },
              }}
            />
            <TextField
              label={intl.formatMessage({
                id: 'citiesAdmin.modal.description',
              })}
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'citiesAdmin.modal.descriptionPlaceholder',
              })}
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#9EC73D' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                  { borderColor: 'rgba(255,255,255,0.5)' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  { borderColor: '#9EC73D' },
              }}
            />
            <Typography variant="subtitle2" sx={{ mt: 2, color: '#fff' }}>
              {intl.formatMessage({ id: 'citiesAdmin.modal.selectCities' })} (
              {intl.formatMessage(
                { id: 'citiesAdmin.modal.selectedCount' },
                { count: selectedCities.length }
              )}
              )
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                placeholder={intl.formatMessage({
                  id: 'citiesAdmin.modal.searchCities',
                })}
                value={modalCitySearch}
                onChange={(e) => setModalCitySearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': { color: '#fff' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    { borderColor: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    { borderColor: '#9EC73D' },
                }}
              />
              <FormControl
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#9EC73D' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    { borderColor: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    { borderColor: '#9EC73D' },
                  '& .MuiSelect-select': { color: '#fff' },
                  '& .MuiSelect-icon': { color: '#fff' },
                }}
              >
                <InputLabel>
                  <FormattedMessage id="citiesAdmin.cities.filter.country" />
                </InputLabel>
                <Select
                  value={modalCountryFilter}
                  onChange={(e) => {
                    setModalCountryFilter(e.target.value)
                    // Clear the state filter — its options depend on country.
                    setModalStateFilter('')
                  }}
                  label={intl.formatMessage({
                    id: 'citiesAdmin.cities.filter.country',
                  })}
                  MenuProps={{
                    PaperProps: {
                      sx: { bgcolor: '#31353D', color: '#fff' },
                    },
                  }}
                >
                  <MenuItem value="">
                    <FormattedMessage id="citiesAdmin.cities.filter.allCountries" />
                  </MenuItem>
                  {uniqueCountries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                disabled={modalUniqueStates.length === 0}
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#9EC73D' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    { borderColor: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    { borderColor: '#9EC73D' },
                  '& .MuiSelect-select': { color: '#fff' },
                  '& .MuiSelect-icon': { color: '#fff' },
                }}
              >
                <InputLabel>
                  <FormattedMessage id="organizations.cityTable.state" />
                </InputLabel>
                <Select
                  value={modalStateFilter}
                  onChange={(e) => setModalStateFilter(e.target.value)}
                  label={intl.formatMessage({
                    id: 'organizations.cityTable.state',
                  })}
                  MenuProps={{
                    PaperProps: {
                      sx: { bgcolor: '#31353D', color: '#fff' },
                    },
                  }}
                >
                  <MenuItem value="">
                    <FormattedMessage id="organizations.cityModal.allStates" />
                  </MenuItem>
                  {modalUniqueStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                bgcolor: '#1a2332',
                border: 1,
                borderColor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <Table
                size="small"
                sx={{
                  '& .MuiTableCell-root': {
                    color: '#fff',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  },
                  '& .MuiTableCell-head': { bgcolor: '#1a2332' },
                  '& .MuiCheckbox-root': { color: 'rgba(255,255,255,0.7)' },
                  '& .MuiCheckbox-root.Mui-checked': { color: '#9EC73D' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          modalFilteredCities.length > 0 &&
                          modalFilteredCities.every((c) =>
                            selectedCities.includes(c.id)
                          )
                        }
                        onChange={(e) =>
                          handleToggleAllCities(e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={modalCitySortBy === 'name'}
                        direction={
                          modalCitySortBy === 'name' ? modalCitySortDir : 'asc'
                        }
                        onClick={() => handleModalCitySort('name')}
                        sx={{
                          color: '#fff !important',
                          '& .MuiTableSortLabel-icon': {
                            color: '#fff !important',
                          },
                        }}
                      >
                        {intl.formatMessage({
                          id: 'citiesAdmin.cities.table.name',
                        })}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={modalCitySortBy === 'country'}
                        direction={
                          modalCitySortBy === 'country'
                            ? modalCitySortDir
                            : 'asc'
                        }
                        onClick={() => handleModalCitySort('country')}
                        sx={{
                          color: '#fff !important',
                          '& .MuiTableSortLabel-icon': {
                            color: '#fff !important',
                          },
                        }}
                      >
                        {intl.formatMessage({
                          id: 'citiesAdmin.cities.table.country',
                        })}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalFilteredCities.map((city) => (
                    <TableRow
                      key={city.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.05) !important',
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCities.includes(city.id)}
                          onChange={() => handleToggleCity(city.id)}
                        />
                      </TableCell>
                      <TableCell>{getLocalizedName(city)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {getLocalizedCountry(city)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} sx={{ color: '#fff' }}>
            <FormattedMessage id="citiesAdmin.modal.cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveGroup}
            disabled={!groupName.trim()}
            sx={{
              bgcolor: '#9EC73D',
              color: '#fff',
              '&:hover': { bgcolor: '#8ab534' },
              '&.Mui-disabled': {
                bgcolor: 'rgba(158,199,61,0.3)',
                color: 'rgba(255,255,255,0.5)',
              },
            }}
          >
            <FormattedMessage id="citiesAdmin.modal.save" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function mapStateToProps(state: {
  AdminStore: Map<string, List<Map<string, unknown>>>
}) {
  return {
    adminStore: state.AdminStore,
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(
      {
        getAllUsersSubscriptions,
        getAllCityGroups,
        createCityGroup,
        updateCityGroup,
        deleteCityGroup,
      },
      dispatch
    ),
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default connect(mapStateToProps, mapDispatchToProps)(CitiesAdmin)
